import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDate,
  MAX_FEEDBACK_TEXT,
  MAX_RAISED_BY,
  type EventSlidePageDto,
  type EventSlidesDto,
  type FeedbackDto,
  type FeedbackStatus,
} from '@pb/shared';
import { getEventSlides, listEvents } from '../api/events';
import {
  deleteFeedback,
  listFeedback,
  recordFeedback,
  recordFeedbackCheck,
  setFeedbackStatus,
  updateFeedback,
  type NewFeedback,
} from '../api/feedback';
import { getGroup } from '../api/groups';
import { listSlideVersions } from '../api/slides';
import { useProject } from '../lib/project';
import { firstLine } from '../lib/text';
import { useResource } from '../lib/useResource';
import { Dialog } from './Dialog';
import { EmptyState, ErrorAlert, ErrorState, Loading } from './States';

export const RAISED_BY_HINT = 'A name or a role, e.g. CFO';

export type FeedbackScope =
  | { kind: 'slide'; slideId: number }
  | { kind: 'group'; groupId: number }
  | { kind: 'event'; eventId: number };

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: 'Open',
  awaiting_confirmation: 'Awaiting confirmation',
  closed: 'Closed',
  dismissed: 'Dismissed',
};

const STATUS_BADGES: Record<FeedbackStatus, string> = {
  open: 'badge badge-warning',
  awaiting_confirmation: 'badge badge-accent',
  closed: 'badge badge-success',
  dismissed: 'badge',
};

export function feedbackTabLabel(openCount: number): string {
  return openCount > 0 ? `Feedback (${openCount})` : 'Feedback';
}

export function feedbackTargetPath(projectId: number, item: Pick<FeedbackDto, 'target_type' | 'target_id'>): string {
  return `/projects/${projectId}/${item.target_type === 'slide' ? 'slides' : 'groups'}/${item.target_id}?tab=feedback`;
}

function scopeFilter(scope: FeedbackScope) {
  if (scope.kind === 'slide') return { slideId: scope.slideId };
  if (scope.kind === 'group') return { groupId: scope.groupId };
  return { eventId: scope.eventId };
}

export function FeedbackCounts({ open, awaiting }: { open: number; awaiting: number }) {
  return (
    <div className="row-wrap" aria-label="Feedback counts">
      <span className={open > 0 ? 'badge badge-warning' : 'badge'}>{open} open</span>
      <span className={awaiting > 0 ? 'badge badge-accent' : 'badge'}>{awaiting} awaiting confirmation</span>
    </div>
  );
}

export function useOpenFeedback(scope: { slideId: number } | { groupId: number }, reloadKey: string | number) {
  const { project } = useProject();
  const key = 'slideId' in scope ? `s${scope.slideId}` : `g${scope.groupId}`;
  return useResource(
    () => listFeedback(project.id, { ...scope, statuses: ['open'] }),
    [project.id, key, reloadKey],
  );
}

export function AnswersSection({
  items,
  chosen,
  onChange,
  disabled,
  hint,
}: {
  items: FeedbackDto[];
  chosen: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  hint?: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="field">
      <span className="field-label" id="feedback-answers-label">
        This version answers
      </span>
      <div className="picker-list" role="group" aria-labelledby="feedback-answers-label">
        {items.map((item) => {
          const on = chosen.includes(item.id);
          return (
            <label key={item.id} className="picker-item checkbox feedback-answer">
              <input
                type="checkbox"
                checked={on}
                disabled={disabled}
                onChange={() => onChange(on ? chosen.filter((id) => id !== item.id) : [...chosen, item.id])}
              />
              <span className="stack grow" style={{ gap: 2 }}>
                <span className="pre" title={item.text}>
                  {firstLine(item.text, 240)}
                </span>
                <span className="row-wrap">
                  {item.raised_by ? (
                    <span className="xsmall muted">Raised by {item.raised_by}</span>
                  ) : (
                    <span className="badge badge-success">closes at once</span>
                  )}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <span className="field-hint">
        A ticked item waits for the person who raised it to confirm this version. An item that names nobody closes at
        once.{hint ? ` ${hint}` : ''}
      </span>
    </div>
  );
}

function pagesLabel(first: number, last: number): string {
  return last > first ? `pp. ${first}–${last}` : `p. ${first}`;
}

interface TargetOption {
  value: string;
  label: string;
}

interface TargetGroup {
  label: string;
  options: TargetOption[];
}

function chapterLabel(page: EventSlidePageDto): string {
  return page.chapter_number !== null ? `${page.chapter_number}. ${page.chapter_title}` : page.chapter_title;
}

function eventTargetGroups(data: EventSlidesDto): TargetGroup[] {
  const groups: TargetGroup[] = [];
  for (const presentation of data.presentations) {
    let current: { key: string; pages: EventSlidePageDto[] } | null = null;
    const chapters: { key: string; pages: EventSlidePageDto[] }[] = [];
    for (const page of presentation.pages) {
      const key = `${page.chapter_number ?? ''}|${page.chapter_title}`;
      if (!current || current.key !== key) {
        current = { key, pages: [] };
        chapters.push(current);
      }
      current.pages.push(page);
    }
    for (const chapter of chapters) {
      const first = chapter.pages[0];
      if (!first) continue;
      const options: TargetOption[] = [];
      const seenGroups = new Set<number>();
      for (const page of chapter.pages) {
        if (!seenGroups.has(page.group_id)) {
          seenGroups.add(page.group_id);
          const members = chapter.pages.filter((p) => p.group_id === page.group_id);
          const start = Math.min(...members.map((p) => p.first_page));
          const end = Math.max(...members.map((p) => p.first_page + Math.max(p.page_count, 1) - 1));
          options.push({ value: `group:${page.group_id}`, label: `Group: ${page.group_name} (${pagesLabel(start, end)})` });
        }
        options.push({
          value: `slide:${page.slide_id}`,
          label: `${pagesLabel(page.first_page, page.first_page + Math.max(page.page_count, 1) - 1)} · ${page.slide_title}`,
        });
      }
      groups.push({ label: `${presentation.name} — ${chapterLabel(first)}`, options });
    }
  }
  return groups;
}

function AddFeedbackForm({
  scope,
  reloadKey,
  onAdded,
}: {
  scope: FeedbackScope;
  reloadKey: number;
  onAdded: () => void;
}) {
  const { project } = useProject();
  const isEvent = scope.kind === 'event';
  const events = useResource(
    () => (isEvent ? Promise.resolve([]) : listEvents(project.id)),
    [project.id, isEvent],
  );
  const eventSlides = useResource(
    () => (scope.kind === 'event' ? getEventSlides(scope.eventId) : Promise.resolve(null)),
    [scope.kind === 'event' ? scope.eventId : 0, reloadKey],
  );
  const [text, setText] = useState('');
  const [raisedBy, setRaisedBy] = useState('');
  const [eventId, setEventId] = useState<number | ''>('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [added, setAdded] = useState(false);

  const targetGroups = eventSlides.data ? eventTargetGroups(eventSlides.data) : [];
  const currentOnly = eventSlides.data?.presentations.filter((p) => p.source === 'current') ?? [];
  const sortedEvents = [...(events.data ?? [])].sort((a, b) => b.date.localeCompare(a.date));
  const canSave = text.trim().length > 0 && (!isEvent || target !== '') && !busy;

  const save = async () => {
    if (!canSave) return;
    const item: NewFeedback = { text: text.trim() };
    if (raisedBy.trim()) item.raised_by = raisedBy.trim();
    if (scope.kind === 'slide') item.slide_id = scope.slideId;
    if (scope.kind === 'group') item.group_id = scope.groupId;
    if (scope.kind === 'event') {
      const [kind, id] = target.split(':');
      if (kind === 'slide') item.slide_id = Number(id);
      else item.group_id = Number(id);
      item.event_id = scope.eventId;
    } else if (eventId !== '') {
      item.event_id = eventId;
    }
    setBusy(true);
    setError(null);
    setAdded(false);
    try {
      await recordFeedback(project.id, [item]);
      setText('');
      setRaisedBy('');
      setAdded(true);
      onAdded();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const idPrefix = `feedback-${scope.kind}`;

  return (
    <section className="panel" aria-label="Add feedback" style={{ padding: 16 }}>
      <h4 style={{ marginBottom: 12 }}>Add feedback</h4>
      <form
        className="form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {isEvent ? (
          <div className="field span-2">
            <label htmlFor={`${idPrefix}-target`}>Slide or group</label>
            {eventSlides.error ? (
              <ErrorState error={eventSlides.error} onRetry={() => void eventSlides.reload()} />
            ) : eventSlides.data === undefined ? (
              <Loading label="Loading the event's slides…" />
            ) : targetGroups.length === 0 ? (
              <p className="small muted">
                This event shows no slides yet. Add a presentation to the event to record feedback here.
              </p>
            ) : (
              <>
                <select
                  id={`${idPrefix}-target`}
                  className="select"
                  value={target}
                  disabled={busy}
                  onChange={(event) => setTarget(event.target.value)}
                >
                  <option value="">Choose a slide or group…</option>
                  {targetGroups.map((group, gi) => (
                    <optgroup key={gi} label={group.label}>
                      {group.options.map((option, oi) => (
                        <option key={`${gi}-${oi}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="field-hint">
                  Page numbers count every page of the deck, hidden pages included, so they can run ahead of what was
                  on screen.
                  {currentOnly.length
                    ? ` Not generated for this event yet, so shown with their current slides: ${currentOnly
                        .map((p) => p.name)
                        .join(', ')}.`
                    : ''}
                </span>
              </>
            )}
          </div>
        ) : null}
        <div className="field span-2">
          <label htmlFor={`${idPrefix}-text`}>Feedback</label>
          <textarea
            id={`${idPrefix}-text`}
            className="textarea"
            value={text}
            maxLength={MAX_FEEDBACK_TEXT}
            placeholder="What was asked or said"
            disabled={busy}
            onChange={(event) => {
              setText(event.target.value);
              setAdded(false);
            }}
          />
        </div>
        <div className="field">
          <label htmlFor={`${idPrefix}-raised-by`}>Raised by (optional)</label>
          <input
            id={`${idPrefix}-raised-by`}
            className="input"
            value={raisedBy}
            maxLength={MAX_RAISED_BY}
            disabled={busy}
            aria-describedby={`${idPrefix}-raised-by-hint`}
            onChange={(event) => setRaisedBy(event.target.value)}
          />
          <span className="field-hint" id={`${idPrefix}-raised-by-hint`}>
            {RAISED_BY_HINT}
          </span>
        </div>
        {isEvent ? null : (
          <div className="field">
            <label htmlFor={`${idPrefix}-event`}>Event (optional)</label>
            <select
              id={`${idPrefix}-event`}
              className="select"
              value={eventId}
              disabled={busy || !events.data}
              onChange={(event) => setEventId(event.target.value ? Number(event.target.value) : '')}
            >
              <option value="">None</option>
              {sortedEvents.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title} — {formatDate(e.date)}
                </option>
              ))}
            </select>
            <span className="field-hint">With an event, the version shown is the one in the event's snapshot.</span>
          </div>
        )}
        <div className="span-2 row-wrap">
          <button type="submit" className="btn btn-primary" disabled={!canSave}>
            {busy ? 'Adding…' : 'Add feedback'}
          </button>
          {added ? (
            <span className="small muted" role="status">
              Feedback added.
            </span>
          ) : null}
        </div>
      </form>
      {events.error ? <ErrorAlert error={events.error} /> : null}
      <ErrorAlert error={error} />
    </section>
  );
}

interface AnswerChoice {
  version_no: number;
  label: string;
}

function AnswerVersionDialog({
  item,
  onClose,
  onDone,
}: {
  item: FeedbackDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const choices = useResource<AnswerChoice[]>(async () => {
    if (item.target_type === 'slide') {
      const versions = await listSlideVersions(item.target_id);
      return versions
        .filter((v) => v.version_no > item.shown_version_no)
        .sort((a, b) => b.version_no - a.version_no)
        .map((v) => ({
          version_no: v.version_no,
          label: `Version ${v.version_no} — ${formatDate(v.created_at)}${v.note ? ` — ${firstLine(v.note, 60)}` : ''}`,
        }));
    }
    const group = await getGroup(item.target_id);
    return group.versions
      .filter((v) => v.version_no >= item.shown_version_no)
      .sort((a, b) => b.version_no - a.version_no)
      .map((v) => ({
        version_no: v.version_no,
        label: `Version ${v.version_no} — ${formatDate(v.created_at)}${v.version_no === item.shown_version_no ? ' (the version shown)' : ''}${v.note ? ` — ${firstLine(v.note, 60)}` : ''}`,
      }));
  }, [item.id]);
  const [picked, setPicked] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const list = choices.data ?? [];
  const value = picked === '' ? (list[0]?.version_no ?? '') : picked;

  const submit = async () => {
    if (value === '') return;
    setBusy(true);
    setError(null);
    try {
      await setFeedbackStatus(item.id, 'awaiting_confirmation', value);
      onDone();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Awaiting confirmation"
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy || value === ''}
          >
            {busy ? 'Saving…' : item.raised_by ? 'Set awaiting confirmation' : 'Close with this version'}
          </button>
        </>
      }
    >
      <p className="text-excerpt">{firstLine(item.text, 300)}</p>
      {choices.error ? (
        <ErrorState error={choices.error} onRetry={() => void choices.reload()} />
      ) : choices.data === undefined ? (
        <Loading label="Loading versions…" />
      ) : list.length === 0 ? (
        <div className="alert alert-info">
          There is no version newer than the one shown (v{item.shown_version_no}). Upload a new version of the slide
          first.
        </div>
      ) : (
        <div className="field">
          <label htmlFor="feedback-answer-version">Answered by</label>
          <select
            id="feedback-answer-version"
            className="select"
            value={value}
            disabled={busy}
            data-autofocus
            onChange={(event) => setPicked(Number(event.target.value))}
          >
            {list.map((choice) => (
              <option key={choice.version_no} value={choice.version_no}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="field-hint">
            {item.target_type === 'slide'
              ? `Only versions newer than the version shown (v${item.shown_version_no}) can answer it.`
              : `The version shown (v${item.shown_version_no}) is allowed too, for a question answered by a new version of a member slide.`}
          </span>
        </div>
      )}
      <p className="small muted">
        {item.raised_by
          ? `Then ask ${item.raised_by} whether this version is clearer, and record the answer with “Clearer” or “Still unclear”.`
          : 'This item names nobody, so it closes at once with this version.'}
      </p>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function EditFeedbackDialog({
  item,
  onClose,
  onDone,
}: {
  item: FeedbackDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState(item.text);
  const [raisedBy, setRaisedBy] = useState(item.raised_by ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const changes: { text?: string; raised_by?: string } = {};
  if (text.trim() !== item.text) changes.text = text.trim();
  if (raisedBy.trim() !== (item.raised_by ?? '')) changes.raised_by = raisedBy.trim();
  const dirty = Object.keys(changes).length > 0;

  const submit = async () => {
    if (!dirty || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await updateFeedback(item.id, changes);
      onDone();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Edit feedback"
      wide
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="edit-feedback-form"
            className="btn btn-primary"
            disabled={busy || !dirty || !text.trim()}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form
        id="edit-feedback-form"
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="edit-feedback-text">Feedback</label>
          <textarea
            id="edit-feedback-text"
            className="textarea"
            value={text}
            maxLength={MAX_FEEDBACK_TEXT}
            rows={6}
            onChange={(event) => setText(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-feedback-raised-by">Raised by (optional)</label>
          <input
            id="edit-feedback-raised-by"
            className="input"
            value={raisedBy}
            maxLength={MAX_RAISED_BY}
            aria-describedby="edit-feedback-raised-by-hint"
            onChange={(event) => setRaisedBy(event.target.value)}
          />
          <span className="field-hint" id="edit-feedback-raised-by-hint">
            {RAISED_BY_HINT}. Leave it empty to name nobody.
          </span>
        </div>
      </form>
      <p className="small muted">The status, the target, the event and the version shown stay as they are.</p>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function DeleteFeedbackDialog({
  item,
  onClose,
  onDone,
}: {
  item: FeedbackDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteFeedback(item.id);
      onDone();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Delete feedback"
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy} data-autofocus>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete feedback'}
          </button>
        </>
      }
    >
      <p>
        Delete feedback item {item.id} on {item.target_type === 'slide' ? 'the slide' : 'the group'} “
        {item.target_title}”? This cannot be undone.
      </p>
      <p className="text-excerpt">{firstLine(item.text, 300)}</p>
      <p className="small muted">To keep a record that it was dealt with, use Dismiss instead.</p>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

function FeedbackRow({
  item,
  showTarget,
  showEvent,
  busy,
  onAction,
  onDialog,
}: {
  item: FeedbackDto;
  showTarget: boolean;
  showEvent: boolean;
  busy: boolean;
  onAction: (run: () => Promise<unknown>) => void;
  onDialog: (kind: 'answer' | 'edit' | 'delete') => void;
}) {
  const { project } = useProject();
  return (
    <li className="card feedback-item" aria-label={`Feedback item ${item.id}`}>
      <div className="row-wrap">
        <span className={STATUS_BADGES[item.status]}>{STATUS_LABELS[item.status]}</span>
        <span className="badge" title={item.shown_at_event ? "From the event's snapshot" : undefined}>
          shown v{item.shown_version_no}
        </span>
        {item.status === 'awaiting_confirmation' && item.answer_version_no !== null ? (
          <span className="badge badge-accent">awaiting v{item.answer_version_no}</span>
        ) : null}
        {item.status === 'closed' && item.answer_version_no !== null ? (
          <span className="badge badge-success">closed by v{item.answer_version_no}</span>
        ) : null}
        {item.status === 'open' && item.check_result === 'still_unclear' && item.answer_version_no !== null ? (
          <span className="badge badge-warning">still unclear with v{item.answer_version_no}</span>
        ) : null}
        {(item.status === 'dismissed' || (item.status === 'open' && item.check_result === null)) && item.answer_version_no !== null ? (
          <span className="badge">awaited v{item.answer_version_no}</span>
        ) : null}
        {showTarget ? (
          <Link to={feedbackTargetPath(project.id, item)} className="small">
            {item.target_type === 'slide' ? 'Slide' : 'Group'}: {item.target_title}
          </Link>
        ) : null}
      </div>
      <div className="pre">{item.text}</div>
      <div className="small muted">
        {item.raised_by ? `Raised by ${item.raised_by}` : 'Raised by nobody named'}
        {showEvent ? (
          item.event_id !== null ? (
            <>
              {' · '}
              <Link to={`/projects/${project.id}/events/${item.event_id}`}>{item.event_title ?? 'Event'}</Link>
            </>
          ) : (
            ' · No event'
          )
        ) : null}
        {' · '}
        {formatDate(item.created_at)}
        {item.created_by ? ` · recorded by ${item.created_by}` : ''}
      </div>
      <div className="row-wrap feedback-actions">
        {item.status === 'open' ? (
          <>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => onDialog('answer')}>
              Awaiting confirmation…
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => onAction(() => setFeedbackStatus(item.id, 'dismissed'))}
            >
              Dismiss
            </button>
          </>
        ) : null}
        {item.status === 'awaiting_confirmation' ? (
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => onAction(() => recordFeedbackCheck(item.id, 'clearer'))}
            >
              Clearer
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => onAction(() => recordFeedbackCheck(item.id, 'still_unclear'))}
            >
              Still unclear
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => onAction(() => setFeedbackStatus(item.id, 'dismissed'))}
            >
              Dismiss
            </button>
          </>
        ) : null}
        {item.status === 'closed' || item.status === 'dismissed' ? (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy}
            onClick={() => onAction(() => setFeedbackStatus(item.id, 'open'))}
          >
            Reopen
          </button>
        ) : null}
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => onDialog('edit')}>
          Edit
        </button>
        <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => onDialog('delete')}>
          Delete
        </button>
      </div>
    </li>
  );
}

export function FeedbackSection({
  scope,
  reloadKey = 0,
  onChanged,
}: {
  scope: FeedbackScope;
  reloadKey?: number;
  onChanged: () => void;
}) {
  const { project } = useProject();
  const filter = scopeFilter(scope);
  const scopeKey = `${scope.kind}:${Object.values(filter)[0]}`;
  const items = useResource(
    () => listFeedback(project.id, { ...filter, sort: 'newest' }),
    [project.id, scopeKey, reloadKey],
  );
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [dialog, setDialog] = useState<{ kind: 'answer' | 'edit' | 'delete'; item: FeedbackDto } | null>(null);

  const all = items.data ?? [];
  const open = all.filter((i) => i.status === 'open').length;
  const awaiting = all.filter((i) => i.status === 'awaiting_confirmation').length;
  const visible = showAll ? all : all.filter((i) => i.status === 'open' || i.status === 'awaiting_confirmation');

  const afterChange = async () => {
    await items.refresh();
    onChanged();
  };

  const run = async (id: number, action: () => Promise<unknown>) => {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err);
    }
    await afterChange();
    setBusyId(null);
  };

  const closeDialog = () => setDialog(null);
  const dialogDone = () => {
    setDialog(null);
    void afterChange();
  };

  return (
    <div className="stack">
      <div className="toolbar">
        <FeedbackCounts open={open} awaiting={awaiting} />
        <span className="spacer" />
        <label className="checkbox">
          <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} />
          Show all
        </label>
      </div>
      <AddFeedbackForm
        scope={scope}
        reloadKey={reloadKey}
        onAdded={() => {
          void afterChange();
        }}
      />
      <ErrorAlert error={actionError} />
      {items.error ? (
        <ErrorState error={items.error} onRetry={() => void items.reload()} />
      ) : items.data === undefined ? (
        <Loading label="Loading feedback…" />
      ) : visible.length === 0 ? (
        all.length === 0 ? (
          <EmptyState title="No feedback yet">
            {scope.kind === 'event'
              ? 'Record what was asked or said in this event about its slides and groups, with the person or role who raised it.'
              : `Record what was asked or said about this ${scope.kind} in a meeting, with the person or role who raised it.`}
          </EmptyState>
        ) : (
          <EmptyState title="Nothing open or awaiting confirmation">
            Every item is closed or dismissed. Turn on “Show all” to see them.
          </EmptyState>
        )
      ) : (
        <ul className="list-plain" aria-label="Feedback items">
          {visible.map((item) => (
            <FeedbackRow
              key={item.id}
              item={item}
              showTarget={scope.kind === 'event'}
              showEvent={scope.kind !== 'event'}
              busy={busyId === item.id}
              onAction={(action) => void run(item.id, action)}
              onDialog={(kind) => setDialog({ kind, item })}
            />
          ))}
        </ul>
      )}
      {dialog?.kind === 'answer' ? <AnswerVersionDialog item={dialog.item} onClose={closeDialog} onDone={dialogDone} /> : null}
      {dialog?.kind === 'edit' ? <EditFeedbackDialog item={dialog.item} onClose={closeDialog} onDone={dialogDone} /> : null}
      {dialog?.kind === 'delete' ? (
        <DeleteFeedbackDialog item={dialog.item} onClose={closeDialog} onDone={dialogDone} />
      ) : null}
    </div>
  );
}
